/**
 * Site Activation Types
 * Defines all TypeScript interfaces for the site activation workflow
 */
import type React from "react";

// Site Workflow Status - represents the overall site lifecycle
export enum SiteWorkflowStatus {
  SELECTED = "SELECTED",
  INITIATION = "INITIATION",
  ACTIVATED = "ACTIVATED",
}

// Enums for status values - matching backend SiteActivationStatus (checklist status)
export enum ActivationStatus {
  DRAFT = "DRAFT",
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  SUBMITTED_FOR_REVIEW = "SUBMITTED_FOR_REVIEW",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  /** @deprecated Use SUBMITTED_FOR_REVIEW instead */
  CHECKLIST_SUBMITTED = "CHECKLIST_SUBMITTED",
  /** @deprecated Use SUBMITTED_FOR_REVIEW instead */
  DOCUMENTS_UNDER_REVIEW = "DOCUMENTS_UNDER_REVIEW",
  /** @deprecated Use APPROVED instead */
  PENDING_APPROVAL = "PENDING_APPROVAL",
  ACTIVATED = "ACTIVATED",
  SUSPENDED = "SUSPENDED",
}

// Legacy alias for backward compatibility
export type ChecklistStatus = ActivationStatus;

export enum DocumentStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum ApprovalStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  SUBMITTED_FOR_REVIEW = "SUBMITTED_FOR_REVIEW",
  APPROVED = "APPROVED",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  ACTIVATED = "ACTIVATED",
  REJECTED = "REJECTED",
  SUSPENDED = "SUSPENDED",
  DRAFT = "DRAFT",
}

export enum DocumentCategory {
  REGULATORY = "REGULATORY",
  STAFFING = "STAFFING",
  TRAINING = "TRAINING",
  INFRASTRUCTURE = "INFRASTRUCTURE",
  BUDGET = "BUDGET",
  PROTOCOL = "PROTOCOL",
  OTHER = "OTHER",
}

// Checklist Item Status enum
export enum ChecklistItemStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  BLOCKED = "BLOCKED",
}

// Evidence Document Type
export interface Evidence {
  documentId: string;
  fileName: string;
  uploadedAt: string;
}

// Checklist Item Source
export type ChecklistItemSource = "GLOBAL" | "STUDY" | "SITE";

// Checklist Item Type
export interface ChecklistItem {
  id: string;
  category: DocumentCategory;
  title: string;
  description: string | null;
  isMandatory: boolean;
  requiresEvidence: boolean;
  sortOrder: number;
  status: ChecklistItemStatus;
  completed: boolean;
  completedAt: string | null;
  completedBy: string | null;
  notes: string | null;
  evidence: Evidence[];
  source?: ChecklistItemSource | string | null;
  isExcluded?: boolean;
  exclusionReason?: string | null;
  excludedBy?: string | null;
  excludedAt?: string | null;
  studyConfigItemId?: string | null;
}

// Activation Checklist Type - matches backend ChecklistResponse
export interface ActivationChecklist {
  id: string | null;
  studyId: string;
  siteId: string;
  status: ActivationStatus;
  siteWorkflowStatus: SiteWorkflowStatus;
  completionPercentage: number;
  createdAt: string | null;
  updatedAt: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  activatedAt: string | null;
  activatedBy: string | null;
  initiationStartedAt: string | null;
  initiationStartedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  items: ChecklistItem[];
  dueDate: string | null;
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  blockers: string[];
  reviewComments: string | null;
  /** Whether the activation has been initialized (template applied) */
  initialized: boolean;
  /** Message shown when not initialized */
  initializationMessage?: string;
}

// Readiness Criterion
export interface ReadinessCriterion {
  id: string;
  name: string;
  description: string;
  met: boolean;
  evidence: Evidence[];
  comments?: string;
  status?: ChecklistItemStatus | string;
  source?: ChecklistItemSource | string | null;
  isExcluded?: boolean;
  exclusionReason?: string | null;
}

// Readiness Category
export interface ReadinessCategory {
  name: string;
  score: number;
  status: ApprovalStatus;
  criteria: ReadinessCriterion[];
}

// Site Readiness Data Type
export interface SiteReadinessData {
  id: string | null;
  studyId: string;
  siteId: string;
  status: ApprovalStatus;
  overallScore: number;
  assessmentDate: string | null;
  assessedBy?: string;
  categories: ReadinessCategory[];
  recommendations?: string[];
  readyForActivation: boolean;
  /** Whether the activation has been initialized (template applied) */
  initialized: boolean;
  craNotes?: string;
  tenantNotes?: string;
  reviewComments?: string | null;
}

// Document Review Type
export interface DocumentReview {
  id: string;
  fileName: string;
  fileSize: string | number;
  fileType: string;
  category: DocumentCategory;
  description: string;
  uploadedAt: string;
  uploadedBy: string;
  status: DocumentStatus;
  approvedAt?: string | null;
  approvedBy?: string | null;
  reviewComments?: string | null;
  rejectReason?: string | null;
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  s3Url?: string;
  /** ID of the checklist item this document is linked to */
  checklistItemId?: string;
  /** Name/title of the linked checklist item */
  checklistItemName?: string;
  /** Current status of the linked checklist item */
  checklistItemStatus?: string;
  source?: ChecklistItemSource | string | null;
  isExcluded?: boolean;
  exclusionReason?: string | null;
}

// Document Review Response Type
export interface DocumentReviewResponse {
  studyId: string;
  siteId: string;
  totalDocuments: number;
  documents: DocumentReview[];
}

// Activation Timeline Event
export interface ActivationTimelineEvent {
  status: ApprovalStatus;
  timestamp: string;
  actor: string;
  /** Resolved display name of the actor (from User.userDetails.fullName) */
  actorName?: string;
  actorType?: "USER" | "SYSTEM";
  description: string;
}

// Activation Workflow Status Type
export interface ActivationWorkflowStatus {
  id: string | null;
  studyId: string;
  siteId: string;
  currentStatus: ApprovalStatus;
  siteWorkflowStatus: SiteWorkflowStatus;
  statusUpdatedAt: string | null;
  activationProgress: number;
  timeline: ActivationTimelineEvent[];
  blockers?: string[];
  nextSteps?: string[];
  estimatedActivationDate?: string;
  activationDate?: string;
  activatedBy?: string;
  initiationStartedAt?: string;
  initiationStartedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  /** Whether the activation has been initialized (template applied) */
  initialized: boolean;
}

// Request/Response Types for API operations

// Checklist Update Request
// All fields optional — which fields are accepted depends on checklist status:
//   NOT_STARTED: definition fields (title, category, description, isMandatory, requiresEvidence)
//   IN_PROGRESS/REJECTED: completion fields (completed, notes, evidenceDocumentId)
export interface UpdateChecklistItemRequest {
  title?: string;
  category?: string;
  description?: string;
  isMandatory?: boolean;
  requiresEvidence?: boolean;
  /** Preferred: set status directly (NOT_STARTED, IN_PROGRESS, COMPLETED) */
  status?: string;
  /** @deprecated Use status field instead */
  completed?: boolean;
  notes?: string;
  evidenceDocumentId?: string;
}

// Readiness Update Request
export interface UpdateSiteReadinessRequest {
  categories?: {
    name: string;
    criteria: {
      id: string;
      met: boolean;
      evidence?: string;
      comments?: string;
    }[];
  }[];
}

// Document Upload Request (FormData)
export interface DocumentUploadRequest {
  file: File;
  category: DocumentCategory;
  description?: string;
}

// Document Approval Request
export interface DocumentApprovalRequest {
  reviewComments?: string;
}

// Document Rejection Request
export interface DocumentRejectionRequest {
  reason: string;
}

// Activation Rejection Request
export interface ActivationRejectionRequest {
  reason: string;
}

// Create Activation Request (simplified flow - templateId optional)
export interface CreateActivationRequest {
  templateId?: string;
  dueDate?: string;
  priority?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
}

// Add Checklist Item Request (TA adds items manually)
export interface AddChecklistItemRequest {
  category: DocumentCategory;
  title: string;
  description?: string;
  isMandatory?: boolean;
  requiresEvidence?: boolean;
  sortOrder?: number;
}

// Submit Checklist Draft Request (TA submits draft to PM)
export interface SubmitChecklistDraftRequest {
  notes?: string;
}

// Submit Checklist Request (CRA submits for review)
export interface SubmitChecklistRequest {
  notes?: string;
  submissionNotes?: string;
}

// Site Activation State for UI
export interface SiteActivationState {
  studyId: string | null;
  siteId: string | null;
  checklist: ActivationChecklist | null;
  readiness: SiteReadinessData | null;
  documents: DocumentReview[];
  workflowStatus: ActivationWorkflowStatus | null;
  loading: boolean;
  error: string | null;
}

// Tab Definitions for UI
export interface ActivationTab {
  id: number;
  label: string;
  icon: React.ReactNode;
  component: React.ComponentType;
}

// Checklist Template Types
export interface ChecklistTemplateItem {
  id?: string;
  category: string;
  title: string;
  description: string;
  isMandatory: boolean;
  requiresEvidence: boolean;
  sortOrder: number;
}

export interface ChecklistTemplate {
  id?: string;
  name: string;
  description: string;
  studyId: string;
  items: ChecklistTemplateItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateChecklistTemplateRequest {
  name: string;
  description: string;
  studyId: string;
  items: ChecklistTemplateItem[];
}

// Dashboard Types
export interface DashboardFilters {
  studyId?: string;
  status?: ActivationStatus;
  priority?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
}

export interface DashboardSiteItem {
  id: string;
  studyId: string;
  studyTitle: string;
  protocolNumber: string;
  siteId: string;
  siteName: string;
  siteCode: string;
  status: ActivationStatus;
  siteWorkflowStatus?: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  completionPercentage: number;
  dueDate: string | null;
  assignedTo: string | null;
  updatedAt: string;
}

export interface DashboardSummary {
  total: number;
  notStarted: number;
  inProgress: number;
  pendingApproval: number;
  activated: number;
  rejected: number;
  averageCompletionPercentage: number;
  overdueCount: number;
}

export interface DashboardResponse {
  items: DashboardSiteItem[];
  // Backward/forward compatibility with backend payload shape.
  activations?: DashboardSiteItem[];
  total: number;
  page: number;
  limit: number;
  summary: DashboardSummary;
}

// Audit History Types
export interface AuditHistoryFilters {
  studyId?: string;
  siteId?: string;
  action?: string;
  page?: number;
  limit?: number;
}

export interface AuditHistoryItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  studyId: string | null;
  studyTitle?: string;
  siteId: string | null;
  siteName?: string;
  actor: string;
  actorId: string;
  details: Record<string, unknown>;
  timestamp: string;
}

export interface AuditHistoryResponse {
  items: AuditHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

// Task Management Types
export enum TaskPriority {
  HIGH = "High",
  MEDIUM = "Medium",
  LOW = "Low",
}

export enum TaskStatus {
  PENDING = "Pending",
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
}

export interface SiteActivationTask {
  id: string;
  taskTitle: string;
  description: string;
  assignedToId: string;
  assignedToName: string;
  assignedToRole: string;
  dueDate: string;
  priority: TaskPriority;
  status: TaskStatus;
  createdBy: string;
  createdByRole?: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  studyId: string;
  siteId: string;
  sourceModule: string;
}

export interface AssignableUser {
  id: string;
  name: string;
  role: string;
  email?: string;
  studyId: string;
  tenantId: string;
}

export interface CreateTaskRequest {
  taskTitle: string;
  description: string;
  assignedToId: string;
  dueDate: string;
  priority: TaskPriority;
  sourceModule?: string;
}

export interface UpdateTaskRequest {
  taskTitle?: string;
  description?: string;
  assignedToId?: string;
  dueDate?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
}

export interface TaskFilters {
  status?: string;
  priority?: string;
  assignedToId?: string;
  searchQuery?: string;
  page?: number;
  limit?: number;
}

export interface TasksResponse {
  tasks: SiteActivationTask[];
  total: number;
  page: number;
  limit: number;
}

// CTMS-144 Study Checklist Configuration Types
export interface StudyChecklistConfigItem {
  id: string;
  studyChecklistConfigId: string;
  category: DocumentCategory | string;
  title: string;
  description: string | null;
  isMandatory: boolean;
  requiresEvidence: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface WaivedGlobalItem {
  id: string;
  studyChecklistConfigId: string;
  globalTemplateItemId: string;
  reason: string;
  status: "APPROVED" | "REJECTED" | "PENDING";
  waivedBy: string;
  waivedAt: string;
}

export interface StudyChecklistConfig {
  id: string;
  studyId: string;
  tenantId: string;
  templateId: string;
  templateVersion: number;
  version: number;
  items: StudyChecklistConfigItem[];
  waivedGlobalItems: WaivedGlobalItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateStudyChecklistConfigRequest {
  templateId: string;
  items?: Array<{
    category: DocumentCategory | string;
    title: string;
    description?: string;
    isMandatory?: boolean;
    requiresEvidence?: boolean;
    sortOrder?: number;
  }>;
}

export interface AddStudyConfigItemRequest {
  category: DocumentCategory | string;
  title: string;
  description?: string;
  isMandatory?: boolean;
  requiresEvidence?: boolean;
  sortOrder?: number;
}

export interface WaiveGlobalItemRequest {
  globalTemplateItemId: string;
  reason: string;
}

export interface ExcludeSiteItemRequest {
  reason: string;
}

export interface AddSiteExceptionItemRequest {
  category: DocumentCategory | string;
  title: string;
  description?: string;
  isMandatory: boolean;
  reason: string;
}
